// lib
import React, { useContext, useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'
import { convertTreeToValues } from '@aytm/app/stats/lib/converter'

// components
import FiltersRow from '@aytm/app/stats/page/sidebar/filters/FiltersRow'
import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'

import AssetFileUploader from '@aytm/app/stats/page/assets/index'
import AssetDeleteJob from '@aytm/app/stats/page/assets/jobs/AssetDeleteJob'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'
const FiltersGroup = ({ group }) => {
  const { root, state, externalData, onShowHiddenGroup, onDNDStart, onDNDEnd, getApiKey } = useContext(DataContext)

  const [hidden, setHidden] = useState(group.hidden || false)
  const [collapsed, setCollapsed] = useState(group.toggle || false)
  const [maxHeight, setMaxHeight] = useState(0)

  const [errorList, setErrorList] = useState([])
  const [processingList, setProcessingList] = useState([])

  const groupRef = useRef()
  const groupUploaderRef = useRef()

  const uploadQueue = useRef([])
  const uploadQueueCallbacks = useRef({})

  const getFilterValues = useCallback(
    (filter, index) => {
      const value = filter.disabled
        ? null
        : group.external
        ? externalData.some(data => data.key == group.key) && externalData.find(data => data.key == group.key).values
          ? externalData.find(data => data.key == group.key).values[index]
          : null
        : null !== state.basic &&
          state.basic[filter.key.toUpperCase()] != undefined &&
          0 < Object.keys(state.basic[filter.key.toUpperCase()]).length
        ? convertTreeToValues(filter, state.basic[filter.key.toUpperCase()], 'value')
        : 'sliderselect' == filter.type
        ? [1, filter.options.length]
        : null

      return null !== value ? JSON.stringify(value) : value
    },
    [group.key, group.external, externalData, state.basic]
  )

  const getFilterOptions = useCallback(
    filter => {
      if (filter.parent) {
        const vals = filter.parent.map(v => state.basic && state.basic[filter.key] && state.basic[filter.key][v])
        const keys = filter.parent.reduce((acc, cur, i) => {
          const value =
            vals[i] != undefined && null !== vals[i] ? (Array.isArray(vals[i].v) ? vals[i].v[0] : vals[i].v) : null
          const parent = group.values.find(v => v.key == filter.key && v.subkey == cur)
          const options = Array.isArray(parent.options)
            ? ['multistatemultiselect', 'multiselect'].includes(parent.type) && parent.group
              ? parent.options.flatMap(option => option.items)
              : parent.options
            : parent.options[acc.join('-')]
          const item = (options != undefined && options.find(opt => opt.value == value)) || null
          acc.push(null !== item ? item.position : null)
          return acc
        }, [])
        return filter.options[keys.join('-')] || []
      }
      return filter.options
    },
    [group.values, state.basic]
  )

  const getFilterError = useCallback(
    filter =>
      Object.hasOwn(filter, 'derived')
        ? errorList.some(key => 0 <= filter.derived.indexOf(key))
        : errorList.includes(filter.key),
    [errorList]
  )
  const getFilterProcessing = useCallback(
    filter =>
      Object.hasOwn(filter, 'derived')
        ? processingList.some(key => 0 <= filter.derived.indexOf(key))
        : processingList.includes(filter.key),
    [processingList]
  )

  const checkQueueCallbacks = () => {
    if (!uploadQueue.current.length) return

    if (uploadQueue.current.length == Object.keys(uploadQueueCallbacks.current).length) {
      let i = 0
      while (!uploadQueueCallbacks.current[uploadQueue.current[i]]) i++

      uploadQueueCallbacks.current[uploadQueue.current[i]]()
      delete uploadQueueCallbacks.current[uploadQueue.current[i]]
    }
  }

  const checkApiKey = async (variable, callback) => {
    if (!variable || 'function' != typeof callback) return false

    const apiKey = await getApiKey()

    uploadQueue.current.push(variable)
    uploadQueueCallbacks.current[variable] = callback

    checkQueueCallbacks()

    return apiKey
  }

  const checkUploadQueue = variable => {
    const index = uploadQueue.current.indexOf(variable)
    uploadQueue.current.splice(index, 1)

    if (0 < uploadQueue.current.length) {
      checkQueueCallbacks()
    } else {
      group.onSuccess()
    }
  }
  const checkUploadQueueError = variable => {
    const index = uploadQueue.current.indexOf(variable)
    uploadQueue.current.splice(index, 1)

    setErrorList([...errorList, variable].filter((v, i, a) => a.indexOf(v) == i))
    setProcessingList(processingList.filter(v => v == variable))

    checkQueueCallbacks()
  }

  const onRemoveVariable = variable => {
    if (0 <= uploadQueue.current.indexOf(variable)) return false

    setErrorList(errorList.filter(v => v == variable))
    setProcessingList([...processingList, variable].filter((v, i, a) => a.indexOf(v) == i))

    checkApiKey(variable, async () => {
      const apiKey = await getApiKey()

      AssetDeleteJob({
        variable,
        ...group.remove,
        params: {
          key: apiKey
        }
      })
        .then(() => {
          if ('function' == typeof group.remove.onVariableRemoved) {
            group.remove.onVariableRemoved(variable)
          }

          checkUploadQueue(variable)
        })
        .catch(() => checkUploadQueueError(variable))
    })

    return true
  }

  useEffect(() => setProcessingList([]), [group.values])

  useEffect(() => {
    setMaxHeight(groupRef.current ? groupRef.current.getBoundingClientRect().height : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.upload, collapsed, groupRef.current])

  return 0 < group.values.length || group.upload ? (
    hidden ? (
      <div
        className={style['filter-switch']}
        onClick={() => {
          onShowHiddenGroup()
          return setHidden(false)
        }}
      >
        Show {group.title.toLowerCase()}
      </div>
    ) : (
      <>
        <div id={`FiltersPanel-group-${group.key}`} className={style['filter-header']}>
          <span className={style['filter-title']}>{group.title}</span>
          {group.help && (
            <SidebarPopup root={root} help={group.help} width={300 < group.help.length ? '600px' : '300px'} />
          )}
          {group.toggle && (
            <span className={style['filter-toggle']} onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? 'EXPAND' : 'COLLAPSE'}
            </span>
          )}
        </div>
        <Transition in={!collapsed} timeout={500}>
          {status => (
            <div
              className={`${style['filter-container']} ${style['transition-panel']}${
                status ? ' ' + style['transition-panel-' + status] : ''
              }`}
              style={{
                maxHeight: group.toggle
                  ? collapsed
                    ? 0
                    : `${0 < maxHeight ? maxHeight + (group.upload ? 30 : 0) : 10000}px`
                  : 'unset'
              }}
            >
              <div ref={groupRef}>
                {group.values.map((filter, index) => (
                  <FiltersRow
                    key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
                    mode={state.mode}
                    group={group}
                    filter={filter}
                    filterValues={getFilterValues(filter, index)}
                    filterOptions={getFilterOptions(filter)}
                    error={getFilterError(filter)}
                    processing={getFilterProcessing(filter)}
                    onRemoveVariable={onRemoveVariable}
                    onDNDStart={onDNDStart}
                    onDNDEnd={onDNDEnd}
                  />
                ))}
                {group.upload && (
                  <div ref={groupUploaderRef} className={`${style['filter-row']} ${style['filter-upload']} p-fluid`}>
                    <AssetFileUploader
                      text={group.upload.text}
                      type={group.upload.type}
                      uploadUrl={group.upload.uploadUrl}
                      statusUrl={group.upload.statusUrl}
                      contentType={group.upload.contentType}
                      upgradePackage={group.upload.upgradePackage}
                      disabled={group.upload.disabled}
                      getAccessKey={async () => await checkApiKey('upload', async () => await getApiKey())}
                      onFileUploaded={async (asset, warnings) => {
                        checkUploadQueue('upload')
                        if ('function' == typeof group.upload.onFileUploaded) {
                          await group.upload.onFileUploaded(asset, warnings)
                          groupUploaderRef.current.scrollIntoView(false)
                        }
                      }}
                      onFileError={errors => {
                        checkUploadQueueError('upload', () => {
                          if ('function' == typeof group.upload.onFileError) {
                            group.upload.onFileError(errors)
                            groupUploaderRef.current.scrollIntoView(false)
                          }
                        })
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </Transition>
      </>
    )
  ) : null
}

FiltersGroup.propTypes = {
  group: PropTypes.object
}

FiltersGroup.displayName = 'FiltersGroup'

export default FiltersGroup

ADND().connect()
